import { useCountries } from 'api/country';
import { deletePlan, downloadPlanFile, IFetchPlanQuery, usePlans } from 'api/plan';
import { useTemplatesList } from 'api/template';
import { useSession } from 'contexts/session';
import { useCheckAuth } from 'hooks/useCheckAuth';
import { useNetErrorHandler } from 'hooks/useNetErrorHandler';
import qs from 'qs';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createDefaultURLSearchParams } from 'utils/net';

export function useUploadPage() {
  useCheckAuth();
  const [session] = useSession();
  const [searchParams, setSearchParams] = useSearchParams(
    createDefaultURLSearchParams({
      limit: 10,
      'sorting[0]': 'createdDate:desc',
      countryId: session?.allowedCountries.length === 1 ? session?.allowedCountries[0].id : '',
    }),
  );
  const handleNetError = useNetErrorHandler();
  const searchQuery = qs.parse(searchParams.toString());
  const { data, isLoading: isPlansLoading, refetch: refetchPlans } = usePlans(searchQuery as IFetchPlanQuery);
  const { data: countries, isFetching: isCountriesLoading } = useCountries({ adminLevel: 0, sorting: ['name:asc'] });
  const { data: adminAreas, isFetching: isAdminAreasLoading } = useCountries(
    { parentId: Number(searchParams.get('countryId')!), adminLevel: 1, sorting: ['name:asc'] },
    { enabled: searchParams.has('countryId') },
  );
  const { data: templates, isFetching: isTemplatesLoading } = useTemplatesList();
  const countryId = searchParams.get('countryId');

  const createHandleDownload = (id: number) => () => downloadPlanFile(id);

  const createHandleDelete = (id: number) => (isConfirmed: boolean) => {
    if (isConfirmed) {
      deletePlan(id)
        .then(() => refetchPlans())
        .catch(handleNetError);
    }
  };

  const changeQuery = (field: string, value: unknown) => {
    if (field === 'countryId') {
      searchParams.delete('adminAreaId');
      searchParams.delete('national');
    }

    if (field === 'adminAreaId') {
      if (value === '0') {
        searchParams.set('national', 'true');
      } else if (value === '-1') {
        searchParams.delete('national');
      }
    }

    if (value === '-1') {
      searchParams.delete(field);
    } else {
      searchParams.set(field, String(value));
    }

    setSearchParams(searchParams);
  };

  const changeSort = (field: string) => {
    const sorting: string[] | undefined = searchQuery.sorting as string[] | undefined;

    if (sorting) {
      const fieldIndex = (sorting as string[]).findIndex(sorting => sorting.startsWith(field));

      if (fieldIndex > -1) {
        let [, direction] = sorting[fieldIndex].split(':');
        searchParams.set('sorting[0]', [field, direction === 'asc' ? 'desc' : 'asc'].join(':'));
      } else {
        searchParams.set('sorting[0]', `${field}:asc`);
      }

      setSearchParams(searchParams);
    }
  };

  const getAdminAreaValue = () => {
    if (searchParams.get('adminAreaId')) {
      return searchParams.get('adminAreaId');
    } else if (searchParams.get('national')) {
      return '0';
    } else {
      return '-1';
    }
  };

  useEffect(() => {
    if (!countryId || countryId === '-1') {
      searchParams.delete('adminAreaId');
    }
  }, [countryId, searchParams]);

  return {
    plans: data?.data,
    countries,
    templates,
    adminAreas,
    dataTotalCount: data?.dataTotalCount,
    searchQuery,
    searchParams,
    isPlansLoading,
    isCountriesLoading,
    isTemplatesLoading,
    isAdminAreasLoading,
    changeQuery,
    changeSort,
    createHandleDownload,
    createHandleDelete,
    setSearchParams,
    getAdminAreaValue,
  };
}
